import { useCallback, useContext, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { CvsTextField } from '@components/index'
import { InsertButton, NameOption } from './insert-button'
import { templateCopyContext } from '../../../context'

interface TemplateCopyContextType {
  onChange: (updateFn: (old: any) => any) => void
  onLoadIntegration: (updateFn: (old: any) => any) => void
}

const Flex = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 20px;
`

const FullWidthTextField = styled(CvsTextField as any)`
  width: 100%;
  margin-left: 8px;
`

interface Props {
  assignment: any
  placeholder: string
  label: string
  metadataKey: string
  nameOptions: NameOption[]
}

export const TextOptionSelector = ({
  assignment,
  placeholder,
  label,
  metadataKey,
  nameOptions,
}: Props) => {
  const { onChange, onLoadIntegration } = useContext(templateCopyContext) as TemplateCopyContextType
  const [selectedNameOptions, setSelectedNameOptions] = useState<NameOption[]>([])

  useEffect(() => {
    if (assignment.metadata[metadataKey] !== undefined) {
      const fileName = assignment.metadata[metadataKey]
      if (!fileName) {
        setSelectedNameOptions([])
        return
      }
      let newOptions = [...selectedNameOptions]
      selectedNameOptions.forEach(nameOption => {
        if (fileName.search(nameOption.value) == -1) {
          newOptions = newOptions.filter(option => option.value !== nameOption.value)
        }
      })
      nameOptions.forEach(option => {
        if (selectedNameOptions.find(nameOption => nameOption.value == option.value)) {
          return
        }
        if (fileName.search(option.value) !== -1) {
          newOptions.push(option)
        }
      })
      setSelectedNameOptions(newOptions)
    }
  }, [assignment.metadata[metadataKey]])

  useEffect(() => {
    if (assignment.filename_pattern) {
      onMetaDataLoad('filename_pattern', assignment.filename_pattern)
    }
  }, [])

  const createMetaDataChangeHandler = updateFn =>
    useCallback(
      (name, value) => {
        updateFn(old => {
          if (name == 'filename_pattern') {
            return {
              ...old,
              [assignment.id]: {
                ...old[assignment.id],
                [name]: value,
                metadata: {
                  ...old[assignment.id].metadata,
                  [name]: value,
                },
              },
            }
          }
          return {
            ...old,
            [assignment.id]: {
              ...old[assignment.id],
              metadata: {
                ...old[assignment.id].metadata,
                [name]: value,
              },
            },
          }
        })
      },
      [assignment, updateFn],
    )

  const onMetaDataChange = createMetaDataChangeHandler(onChange)
  const onMetaDataLoad = createMetaDataChangeHandler(onLoadIntegration)

  const updateNameOptions = (option: NameOption) => {
    const newNameOptions = [...selectedNameOptions, option]
    const currentFileName = assignment.metadata[metadataKey] || ''
    const newFileName = currentFileName.concat('', option.value)
    onMetaDataChange(metadataKey, newFileName)
    setSelectedNameOptions(newNameOptions)
  }

  const handleFileNameChange = event => {
    onMetaDataChange(metadataKey, event.target.value)
  }

  const availableNameOptions: NameOption[] = nameOptions.filter(
    nameOption => !selectedNameOptions.find(s => s.value == nameOption.value),
  )

  return (
    <Flex>
      <FullWidthTextField
        placeholder={placeholder}
        label={label}
        value={assignment.metadata[metadataKey] || ''}
        onChange={value => {
          handleFileNameChange(value)
        }}
        style={{ margin: 0 }}
      />
      <InsertButton nameOptions={availableNameOptions} handleOptionClick={updateNameOptions} />
    </Flex>
  )
}
